module Widgets.Form.Access where

import Common

import Data.Argonaut (class DecodeJson, class EncodeJson, decodeJson, encodeJson)
import Data.Array as Arr
import Data.Int as Int
import Data.Maybe (isJust)
import Data.String (Pattern(..))
import Data.String as Str


data FieldAccessLevel
    = AccessNone
    | AccessView
    | AccessUpdate

derive instance eqFieldAccessLevel :: Eq FieldAccessLevel
derive instance ordFieldAccessLevel :: Ord FieldAccessLevel

instance showFieldAccessLevel :: Show FieldAccessLevel where
    show AccessNone = "NONE"
    show AccessView = "VIEW"
    show AccessUpdate = "UPDATE"

instance decodeFieldAccessLevel :: DecodeJson FieldAccessLevel where
    decodeJson json = do
        level :: String <- decodeJson json
        case level of
            "NONE" -> pure AccessNone
            "VIEW" -> pure AccessView
            "UPDATE" -> pure AccessUpdate
            _ -> Left $ UnexpectedValue json

instance encodeFieldAccessLevel :: EncodeJson FieldAccessLevel where
    encodeJson AccessNone = encodeJson "NONE"
    encodeJson AccessView = encodeJson "VIEW"
    encodeJson AccessUpdate = encodeJson "UPDATE"

type FormFieldAccess =
    { field :: String
    , access :: FieldAccessLevel
    }

type FormAccess =
    { default :: FieldAccessLevel
    , fields :: Array FormFieldAccess
    }

fullFormAccess :: FormAccess
fullFormAccess =
    { default: AccessUpdate
    , fields: []
    }

canView :: FieldAccessLevel -> Boolean
canView AccessNone = false
canView _ = true

canUpdate :: FieldAccessLevel -> Boolean
canUpdate AccessUpdate = true
canUpdate _ = false

resolveFieldAccess :: FormAccess -> String -> FieldAccessLevel
resolveFieldAccess access path =
    fromMaybe access.default $ _.access <$> best
  where
    pathParts = toPathParts path

    best = foldl pickBest Nothing access.fields

    pickBest
        :: Maybe { depth :: Int, access :: FieldAccessLevel }
        -> FormFieldAccess
        -> Maybe { depth :: Int, access :: FieldAccessLevel }
    pickBest current entry = case matchDepth entry of
        Nothing -> current
        Just depth -> case current of
            Nothing -> Just { depth, access: entry.access }
            Just prev | depth >= prev.depth -> Just { depth, access: entry.access }
            Just prev -> Just prev

    matchDepth :: FormFieldAccess -> Maybe Int
    matchDepth { field } =
        let fieldParts = toPathParts field in
        if isPrefix fieldParts pathParts
            then Just $ Arr.length fieldParts
            else Nothing

hasVisibleDescendant :: FormAccess -> String -> Boolean
hasVisibleDescendant access path = any isVisibleDescendant access.fields
  where
    pathParts = toPathParts path

    isVisibleDescendant { field, access: level } =
        let fieldParts = toPathParts field in
        Arr.length fieldParts > Arr.length pathParts
            && isPrefix pathParts fieldParts
            && canView level

toPathParts :: String -> Array String
toPathParts =
    Arr.filter (\part -> part /= "" && not (isIndexPart part))
        <<< Str.split (Pattern ".")

isIndexPart :: String -> Boolean
isIndexPart part = isJust $ Int.fromString part

isPrefix :: Array String -> Array String -> Boolean
isPrefix prefix full = prefix == Arr.take (Arr.length prefix) full
